var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../Base.js";
import LocaleManager from "./LocaleManager.js";
import LocaleHelper from "./LocaleHelper.js";
import Objects from "../helper/util/Objects.js";
const ObjectProto = Object.getPrototypeOf(Object), localeRe = /L{.*?}/g, capturelocaleRe = /L{(.*?)}/g, classMatchRe = /((.*?)\.)?(.+)/g, escape = (txt) => txt.replace(/{(\d+)}/gm, "[[$1]]"), unescape = (txt) => txt.replace(/\[\[(\d+)]]/gm, "{$1}"), emptyObject = Object.freeze(/* @__PURE__ */ Object.create(null));
var Localizable_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * A class translations of which are used for translating this entity.
         * This is often used when translations of an item are defined on its container class.
         * For example:
         *
         * ```javascript
         * // Toolbar class that has some predefined items
         * class MyToolbar extends Toolbar {
         *
         *     static $name = 'MyToolbar';
         *
         *     static get defaultConfig() {
         *         return {
         *             // this specifies default configs for the items
         *             defaults : {
         *                 // will tell items to use the toolbar locale
         *                 localeClass : this
         *             },
         *
         *             items : [
         *                 // The toolbar has 2 buttons and translation for their texts will be searched in
         *                 // the toolbar locales
         *                 { text : 'Agree' },
         *                 { text : 'Disagree' }
         *             ]
         *         };
         *     }
         *
         *    ...
         * }
         * ```
         * So if one makes a locale for the `MyToolbar` class that will include `Agree` and `Disagree` string translations:
         * ```javascript
         *     ...
         *     MyToolbar : {
         *         Agree    : 'Yes, I agree',
         *         Disagree : 'No, I do not agree'
         *     }
         * ```
         * They will be used for the toolbar buttons and the button captions will say `Yes, I agree` and `No, I do not agree`.
         *
         * @config {Core.Base}
         * @typings {typeof Base}
         * @category Misc
         * @advanced
         */
        localeClass: null,
        /**
         * Set to `false` to disable localization of this object.
         * @config {Boolean}
         * @default true
         * @category Misc
         * @advanced
         */
        localizable: null,
        /**
         * List of properties which values should be translated automatically upon a locale applying.
         * In case there is a need to localize not typical value (not a String value or a field with re-defined setter/getter),
         * you could use 'localeKey' meta configuration.
         * Example:
         * ```javascript
         *  static get configurable() {
         *     return {
         *          localizableProperties : ['width'],
         *
         *          width : {
         *              value   : '54em', // default value here
         *              $config : {
         *                  localeKey : 'L{editorWidth}' // name of the property that will be used in localization file
         *              }
         *          }
         *      };
         *  }
         * ```
         * @config {String[]}
         * @category Misc
         * @advanced
         */
        localizableProperties: {
          value: [],
          $config: {
            merge: "distinct"
          }
        }
      };
    }
    static clsName(cls) {
      var _a2, _b;
      return typeof cls === "string" ? cls : cls === ObjectProto ? "Object" : cls.$$name || cls.name || ((_a2 = cls.prototype) == null ? void 0 : _a2.$$name) || ((_b = cls.prototype) == null ? void 0 : _b.name);
    }
    static parseLocaleString(text) {
      const matches = [];
      let m;
      if (text == null ? void 0 : text.includes("L{")) {
        text = escape(text);
        capturelocaleRe.lastIndex = 0;
        while ((m = capturelocaleRe.exec(text)) != null) {
          classMatchRe.lastIndex = 0;
          const classMatch = classMatchRe.exec(m[1]);
          matches.push({
            match: unescape(m[0]),
            localeKey: unescape(classMatch[3]),
            localeClass: classMatch[2]
          });
        }
      }
      return matches.length > 0 ? matches : [{
        match: text,
        localeKey: text,
        localeClass: void 0
      }];
    }
    construct(config = {}, ...args) {
      super.construct(config, ...args);
      LocaleManager.ion({ locale: "updateLocalization", thisObj: this });
      this.updateLocalization();
    }
    get localeClass() {
      return this._localeClass || null;
    }
    localizeProperty(property) {
      var _a2, _b, _c;
      const me = this, currentValue = Objects.getPath(me, property), localeKey = ((_a2 = me.$meta.configs[property]) == null ? void 0 : _a2.localeKey) || ((_c = (_b = me.fieldMap) == null ? void 0 : _b[property]) == null ? void 0 : _c.defaultValue);
      let localizedValue;
      if (localeKey) {
        localizedValue = _a.localize(localeKey, me, me.localeClass || me);
        if (localizedValue && !(property in (me.initialConfig || emptyObject)) && !(property in (me.originalData || emptyObject))) {
          Objects.setPath(me.isColumn ? me.data : me, property, localizedValue);
        } else {
          return currentValue;
        }
      } else if (typeof currentValue === "string") {
        me.originalLocales = me.originalLocales || {};
        localizedValue = Objects.getPath(me.originalLocales, property);
        if (localizedValue === void 0) {
          Objects.setPath(me.originalLocales, property, currentValue);
          localizedValue = currentValue;
        }
        if (localizedValue) {
          Objects.setPath(me, property, localizedValue = me.optionalL(localizedValue, me));
        }
      }
      return localizedValue || currentValue;
    }
    /**
     * Method that is triggered when applying a locale to the instance
     * (happens on the instance construction steps and when switching to another locale).
     *
     * The method can be overridden to dynamically translate the instance when locale is switched.
     * When overriding the method please make sure you call `super.updateLocalization()`.
     * @category Misc
     * @advanced
     */
    updateLocalization() {
      var _a2, _b;
      if (this.localizable !== false) {
        (_a2 = this.localizableProperties) == null ? void 0 : _a2.forEach(this.localizeProperty, this);
        (_b = this.trigger) == null ? void 0 : _b.call(this, "localized");
      }
    }
    static getTranslation(text, templateData, localeCls) {
      const locale = LocaleManager.locale;
      let result = null, clsName, cls;
      if (locale) {
        for (const { match, localeKey, localeClass } of this.parseLocaleString(text)) {
          const translate = (clsName2) => {
            var _a2;
            const translation = (_a2 = locale[clsName2]) == null ? void 0 : _a2[localeKey];
            if (translation) {
              if (typeof translation === "function") {
                result = templateData != null ? translation(templateData) : translation;
              } else if (typeof translation === "object" || text === match) {
                result = translation;
              } else {
                result = (result || text).replace(match, translation);
              }
              if (typeof translation === "string" && translation.includes("L{")) {
                result = this.getTranslation(translation, templateData, localeCls);
              }
            }
            return translation;
          };
          let success = false;
          for (cls = localeCls; cls && (clsName = _a.clsName(cls)); cls = Object.getPrototypeOf(cls)) {
            if (success = translate(clsName)) {
              break;
            } else if (typeof cls === "string") {
              break;
            }
          }
          if (!success && localeClass) {
            translate(localeClass);
          }
        }
      }
      return result;
    }
    /**
     * Get localized string, returns `null` if no localized string found.
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @internal
     */
    static localize(text, templateData = void 0, ...localeClasses) {
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      let translation = null;
      localeClasses.some((cls) => {
        translation = _a.getTranslation(text, templateData, cls);
        return translation != null;
      });
      return translation;
    }
    /**
     * Get localized string, returns value of `text` if no localized string found.
     *
     * If {@link Core.localization.LocaleManager#property-throwOnMissingLocale LocaleManager.throwOnMissingLocale}
     * is `true` then calls to `L()` will throw `Localization is not found for 'text' in 'ClassName'` exception when no
     * localization is found.
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @static
     * @returns {String}
     * @advanced
     */
    static L(text, templateData = void 0, ...localeClasses) {
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      const translation = this.localize(text, templateData, ...localeClasses);
      if (translation == null && LocaleManager.throwOnMissingLocale && text.includes("L{")) {
        throw new Error(`Localization is not found for '${text}' in '${localeClasses.map((cls) => _a.clsName(cls)).join(", ")}'. ${LocaleManager.locale.localeName ? `Locale : ${LocaleManager.locale.localeName}` : ""}`);
      }
      return translation != null ? translation : text;
    }
    /**
     * Convenience function that can be called directly on the class that mixes Localizable in
     *
     * ```javascript
     * button.text = grid.L('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @category Misc
     * @advanced
     */
    L(text, templateData, localeClass = this.localeClass) {
      const { constructor } = this;
      if (localeClass && _a.clsName(localeClass) !== _a.clsName(constructor)) {
        return _a.L(text, templateData, localeClass, constructor);
      } else {
        return _a.L(text, templateData, constructor);
      }
    }
    /**
     * Convenience function to get an optional translation. The difference compared to `L()` is that it won't throw
     * an error when the translation is missing even if configured with `throwOnMissingLocale`
     *
     * ```javascript
     * button.text = grid.optionalL('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @returns {String}
     * @static
     * @category Misc
     * @advanced
     */
    static optionalL(text, templateData = void 0, ...localeClasses) {
      const shouldThrow = LocaleManager.throwOnMissingLocale;
      LocaleManager.throwOnMissingLocale = shouldThrow && localeRe.test(text);
      if ((localeClasses == null ? void 0 : localeClasses.length) === 0) {
        localeClasses = [this];
      }
      const result = _a.L(text, templateData, ...localeClasses);
      LocaleManager.throwOnMissingLocale = shouldThrow;
      return result;
    }
    /**
     * Convenience function to get an optional translation. The difference compared to `L()` is that it won't throw
     * an error when the translation is missing even if configured with `throwOnMissingLocale`
     *
     * ```javascript
     * button.text = grid.optionalL('L{group}');
     * ```
     *
     * @param {String} text String key
     * @param {Object} [templateData] Data to supply to template if localized string is a function
     * @param {Boolean} [preventThrow] Prevent throwing error even if localized text matches `L{foo}`
     * @returns {String}
     * @category Misc
     * @internal
     */
    optionalL(text, templateData = this, preventThrow = false) {
      const shouldThrow = LocaleManager.throwOnMissingLocale;
      LocaleManager.throwOnMissingLocale = shouldThrow && localeRe.test(text) && !preventThrow;
      const result = this.L(text, templateData);
      LocaleManager.throwOnMissingLocale = shouldThrow;
      return result;
    }
    /**
     * Get the global LocaleManager
     * @property {Core.localization.LocaleManager}
     * @typings {typeof LocaleManager}
     * @category Misc
     * @readonly
     * @advanced
     */
    get localeManager() {
      return LocaleManager;
    }
    /**
     * Get the global LocaleHelper
     * @property {Core.localization.LocaleHelper}
     * @typings {typeof LocaleHelper}
     * @category Misc
     * @readonly
     * @advanced
     */
    get localeHelper() {
      return LocaleHelper;
    }
  }, __publicField(_a, "$name", "Localizable"), _a;
};
export {
  Localizable_default as default
};
